/**
 * Swedish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Swedish at a mathematician's register. **Warrant → «Berättigande»**
 * (epistemic justification; the Scandinavian Toulmin term «hjemmel» is Danish/Norwegian, not
 * Swedish — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / other, so `frameActive` declares exactly those (plus an explicit `=0`).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const SV_CATALOG = {
    locale: 'sv',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Undersökning',
        [P2.sectionClaims]: 'Påståenden',
        [P2.sectionIntegrity]: 'Integritet',
        [P2.axisWarrantLabel]: 'Berättigande',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirisk',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definition} interp {Tolkning} testable {Testbar} tested {Testad} refuted {Vederlagd} program {Forskningsprogram} philos {Filosofisk} other {Okänd}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Styrkenivå {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Utforskbar',
        [P2.stateRefuted]: 'Vederlagd',
        [P2.stateOutside]: 'Utanför området',
        [P2.verdictConflict]: 'I konflikt med {constraints}',
        [P2.verdictOutside]: 'Kräver {missing}',
        [P2.lossRegisterPlain]: 'Förenklat till vanligt språk — en del av precisionen utelämnas.',
        [P2.lossRegisterKid]: 'Förenklat för yngre läsare — många detaljer utelämnas.',
        [P2.lossProfileDetail]: 'Den fullständiga profilen Berättigande / Empirisk / Modal sammanfattas här.',
        [P2.lossConflictDetail]: 'Konfliktdetaljerna är förkortade i den här vyn.',
        [P2.laneVerified]: 'Verifierad',
        [P2.laneUnverified]: 'Ej verifierad',
        [P2.laneConflict]: 'Konflikt bevisad',
        [P2.constraintLogic]: 'Logik',
        [P2.constraintLightBends]: 'Ljus böjs i ett gravitationsfält',
        [P2.constraintParallelPostulate]: 'Parallellpostulatet',
        [P2.claimScalarGravity]: 'Skalär gravitationsteori',
        [P2.claimNonEuclidean]: 'Icke-euklidisk geometri',
        [P2.claimSubstrateSr]: 'Substrattolkning av den speciella relativitetsteorin',
        [P2.edgeExtObservation]: 'Extern observation',
        [P2.edgeResourceSample]: 'Delad beräkningsresurs',
        [P2.edgeHeuristicSample]: 'Heuristisk vägledning',
        [P2.edgeSharedSample]: 'Gemensam grund',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Begränsningar',
        [P2.sectionEdges]: 'Kanter',
        [P2.sectionFrame]: 'Ram',
        [P2.sectionExposure]: 'Exponering',
        [P2.statusAdoptedFormal]: 'Antagen (formell)',
        [P2.statusAdoptedInterpretive]: 'Antagen (tolkande)',
        [P2.statusSuspended]: 'Suspenderad',
        [P2.edgeEffectConstraint]: 'Bildar en begränsning (kan vederlägga)',
        [P2.edgeEffectResource]: 'Resurs — ingen effekt på utslag',
        [P2.edgeEffectHeuristic]: 'Heuristik — ingen effekt på utslag',
        [P2.edgeEffectShared]: 'Gemensam grund — ingen effekt på utslag',
        [P2.frameActive]: '{count, plural, =0 {Inga aktiva begränsningar} one {# aktiv begränsning} other {# aktiva begränsningar}}',
        [P2.exposureNone]: 'Inga tolkande beroenden',
        [P2.exposureDep]: 'Beror på {id}',
        [P2.laneTrackMachine]: 'Maskin',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Gemenskap',
        [P2.expertAudited]: 'Granskad',
        [P2.expertInProgress]: 'Pågår',
        [P2.expertNotReviewed]: 'Ej bedömd',
        [P2.communityFlags]: 'Markeringar finns',
        [P2.communityNone]: 'Inga markeringar',
        [P2.controlChallenge]: 'Bestrid',
        [P2.controlActivate]: 'Aktivera',
        [P2.controlMakeInterpretive]: 'Gör tolkande',
        [P2.controlImportEdge]: 'Importera kant',
        [P2.controlPromote]: 'Befordra',
        [P2.controlUndo]: 'Ångra',
        [P2.controlRedo]: 'Gör om',
        [P2.controlRegister]: 'Registrera',
        [P2.promoteRejected]: 'Befordran avvisad',
        [P2.promoteAccepted]: 'Befordran godtagen',
        [P2.promoteReasonRefutationTerminal]: 'Ett bevis kan inte läka en vederläggning.',
        [P2.promoteReasonKeystone]: 'En slutstensbefordran kräver empiriskt stöd.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Den empiriska typen är inte fastställd.',
        [P2.promoteReasonModalNotFixed]: 'Den modala graden är inte fastställd.',
        [P2.promoteReasonWarrantNotImproved]: 'Berättigandegraden har inte förbättrats.',
        [P2.evidenceComputation]: "{engine} beräknade {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Resultat avrundat till {dp} decimaler",
    },
};
//# sourceMappingURL=sv.js.map